import { useAuth } from '@/contexts/auth-context';
import { useBillingActionScope } from '@/hooks/actions/use-billing-action-scope';
import { billingContextQueryKey } from '@/hooks/queries/use-billing-context-query';
import api from '@/libs/axiosInstance';
import { readBillingContext } from '@/libs/billing-access';
import type { ApiResponse, OrganizationBillingContext } from '@fluenceur/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export const CARD_SETUP_STATUS_QUERY_KEY = ['billingCardSetupStatus'] as const;

/** Reconcile only an operation already named by the current server context. */
export function useCardSetupStatusQuery({
  context,
  enabled = true,
}: {
  context?: OrganizationBillingContext;
  enabled?: boolean;
}) {
  const { isLoading } = useAuth();
  const { scope, assertCurrent } = useBillingActionScope();
  const client = useQueryClient();
  const canReadOperation =
    !!context &&
    !!readBillingContext(context) &&
    context.organizationId === scope.organizationId &&
    context.brandId === scope.brandId &&
    !!context.cardSetup.operation &&
    context.capabilities.canCommitPaidPlan &&
    context.cardSetup.exemption !== 'impersonation';
  const operationId = canReadOperation ? context.cardSetup.operation?.id : null;
  const query = useQuery<OrganizationBillingContext>({
    queryKey: [...CARD_SETUP_STATUS_QUERY_KEY, scope.organizationId, scope.brandId, operationId],
    enabled: enabled && !isLoading && canReadOperation,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) => {
      assertCurrent(scope);
      if (!canReadOperation || !operationId) throw new Error('No authorized card setup operation');
      const response = await api.get<ApiResponse<unknown>>(
        `/dashboard/billing/card-setup/${encodeURIComponent(operationId)}`,
        { signal }
      );
      assertCurrent(scope);
      const next = readBillingContext(response.data.data);
      if (
        response.data.success !== true ||
        !next ||
        next.organizationId !== scope.organizationId ||
        next.brandId !== scope.brandId
      ) {
        throw new Error('Invalid card setup status context');
      }
      const contextKey = billingContextQueryKey(scope);
      await client.cancelQueries({ queryKey: contextKey, exact: true });
      assertCurrent(scope);
      client.setQueryData(contextKey, next);
      return next;
    },
  });
  return { ...query, canReadOperation };
}
